import React, { useCallback, useEffect, useState } from 'react';
import {
  FileDown,
  FolderCheck,
  FolderPlus,
  HardDriveDownload,
  LoaderCircle,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Settings,
  Square,
  TestTubeDiagonal,
  Trash2,
  Wrench,
} from 'lucide-react';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import {
  cancelBackup,
  createJenkinsConfig,
  createMailHogConfig,
  deleteBackupJob,
  deleteJenkinsConfig,
  deleteMailHogConfig,
  downloadBackup,
  getBackupJob,
  getBackupJobs,
  getJenkinsConfigs,
  getMailHogConfigs,
  setupJenkinsFolder,
  setupMailHogFolder,
  takeJenkinsBackup,
  takeMailHogBackup,
  testJenkinsConfig,
  testMailHogConfig,
  updateJenkinsConfig,
  updateMailHogConfig,
} from '../../Services/BackupService';
import {
  BACKUP_SOURCE_LABELS,
  BACKUP_STATUSES,
  BACKUP_STATUS_LABELS,
  BackupJob,
  BackupSource,
  BackupStatus,
  FolderStatus,
  JenkinsConfig,
  JenkinsConfigRequest,
  MailHogConfig,
  MailHogConfigRequest,
} from '../../Types/BackupType';

const PAGE_SIZE = 10;

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const statusTone: Record<BackupStatus, string> = {
  SUCCESS: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  FAILED: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  RUNNING: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  PENDING: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  CANCELLED: 'border-slate-600 bg-slate-800/60 text-slate-400',
};

const formatBytes = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDuration = (millis?: number | null): string => {
  if (millis === null || millis === undefined || millis < 0) return '—';
  if (millis < 1000) return `${Math.round(millis)}ms`;
  const totalSeconds = Math.round(millis / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const downloadFallbackName = (job: BackupJob): string => {
  const raw = job.startedAt || job.createdAt;
  if (!raw) return `nexgent-backup_${job.id}.zip`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return `nexgent-backup_${job.id}.zip`;
  const month = MONTHS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours12 = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = date.getHours() < 12 ? 'AM' : 'PM';
  return `nexgent-backup_${month}-${day}-${year}_at-${hours12}-${minutes}-${ampm}_${job.id}.zip`;
};

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[10px] font-semibold text-slate-500">{hint}</p>}
  </label>
);

const emptyJenkinsForm: JenkinsConfigRequest = {
  name: '',
  baseUrl: 'http://localhost:8080',
  username: '',
  apiToken: '',
  baseFolderPath: '',
  active: true,
};

const emptyMailHogForm: MailHogConfigRequest = {
  name: 'MailHog (local)',
  apiBaseUrl: 'http://localhost:8025',
  username: '',
  password: '',
  sshHost: '',
  sshPort: 22,
  sshUsername: '',
  sshPassword: '',
  sshPrivateKey: '',
  sshKeyPassphrase: '',
  baseFolderPath: '',
  active: true,
};

/**
 * Business Support > Take Backup — Jenkins and MailHog sources.
 *
 * One parameterized panel for both non-database sources: a Backups tab listing runs filtered
 * by backupType with live progress and download, and a Config tab holding the connection
 * settings (Jenkins user + API token; MailHog API URL plus optional Basic-auth credentials
 * for a proxy in front of MailHog, plus optional SSH credentials to pull the server-side
 * deployment files). The database source keeps its
 * own richer panel in TakeBackupComponent.
 */
function SourceBackupComponent({ source }: { source: Extract<BackupSource, 'JENKINS' | 'MAILHOG'> }) {
  const isJenkins = source === 'JENKINS';
  const label = BACKUP_SOURCE_LABELS[source];

  const [tab, setTab] = useState<'backups' | 'config'>('backups');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  // ---- Runs -------------------------------------------------------
  const [jobs, setJobs] = useState<PaginationResponse<BackupJob> | null>(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [confirmJobId, setConfirmJobId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // ---- Audit log viewer --------------------------------------------
  const [logsJobId, setLogsJobId] = useState<string | null>(null);
  const [logsJob, setLogsJob] = useState<BackupJob | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // ---- Trigger ----------------------------------------------------
  const [configs, setConfigs] = useState<(JenkinsConfig | MailHogConfig)[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [taking, setTaking] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);

  // ---- Live run ----------------------------------------------------
  const [liveJob, setLiveJob] = useState<BackupJob | null>(null);
  const [stopping, setStopping] = useState(false);

  // ---- Config form -------------------------------------------------
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<JenkinsConfig | MailHogConfig | null>(null);
  const [jenkinsForm, setJenkinsForm] = useState<JenkinsConfigRequest>(emptyJenkinsForm);
  const [mailHogForm, setMailHogForm] = useState<MailHogConfigRequest>(emptyMailHogForm);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [confirmConfigId, setConfirmConfigId] = useState<string | null>(null);

  const selected = configs.find((config) => config.id === selectedId) || null;

  const showError = useCallback((message: string) => {
    setAlert({ variant: 'error', message });
  }, []);

  const loadConfigs = useCallback(async () => {
    const response = isJenkins ? await getJenkinsConfigs() : await getMailHogConfigs();
    if (response.success) {
      const list = response.data || [];
      setConfigs(list);
      setSelectedId((previous) => {
        if (previous && list.some((config) => config.id === previous)) return previous;
        return list.length ? list[0].id : '';
      });
    } else {
      showError(response.message);
    }
  }, [isJenkins, showError]);

  const loadJobs = useCallback(
    async (targetPage: number) => {
      setJobsLoading(true);
      const response = await getBackupJobs({
        backupType: source,
        page: targetPage,
        size: PAGE_SIZE,
        status: statusFilter,
        search,
        fromDate,
        toDate,
      });
      setJobsLoading(false);
      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        showError(response.message);
      }
    },
    [source, statusFilter, search, fromDate, toDate, showError]
  );

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    loadJobs(page);
  }, [loadJobs, page]);

  // Refresh the list while a run is still in flight.
  const isRunning = Boolean(
    jobs && jobs.content.some((job) => job.status === 'RUNNING' || job.status === 'PENDING')
  );
  useEffect(() => {
    if (!isRunning) return undefined;
    const timer = window.setInterval(() => loadJobs(page), 5000);
    return () => window.clearInterval(timer);
  }, [isRunning, page, loadJobs]);

  const runningJob =
    jobs?.content.find((job) => job.status === 'RUNNING' || job.status === 'PENDING') || null;

  // Poll the in-flight run every 2s for the percentage bar, ETA and audit lines.
  useEffect(() => {
    if (!runningJob) {
      setLiveJob(null);
      return undefined;
    }
    let active = true;
    const poll = async () => {
      const response = await getBackupJob(runningJob.id);
      if (active && response.success && response.data) setLiveJob(response.data);
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [runningJob?.id]);

  const resetPage = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const canTakeBackup = Boolean(selected) && selected?.folderExists;

  const handleTakeBackup = async () => {
    if (!selected) return;
    setTaking(true);
    const response = isJenkins
      ? await takeJenkinsBackup(selected.id)
      : await takeMailHogBackup(selected.id);
    setTaking(false);
    if (response.success && response.data) {
      setAlert({ variant: 'success', message: `${label} backup started.` });
      loadJobs(0);
      setPage(0);
    } else {
      showError(response.message);
    }
  };

  const handleStopRun = async () => {
    if (!runningJob) return;
    setStopping(true);
    const response = await cancelBackup(runningJob.id);
    setStopping(false);
    if (response.success) {
      setAlert({ variant: 'success', message: 'Stop requested — the run will cancel at its next checkpoint.' });
      loadJobs(page);
    } else {
      showError(response.message);
    }
  };

  const handleDownload = async (job: BackupJob) => {
    setDownloadingId(job.id);
    try {
      await downloadBackup(job.id, downloadFallbackName(job));
    } catch (error: any) {
      showError(error.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const openJobLogs = async (job: BackupJob) => {
    setLogsJobId(job.id);
    setLogsJob(job);
    setLogsLoading(true);
    const response = await getBackupJob(job.id);
    setLogsLoading(false);
    if (response.success && response.data) setLogsJob(response.data);
  };

  const handleDeleteJob = async (id: string) => {
    setConfirmJobId(null);
    const response = await deleteBackupJob(id);
    if (response.success) {
      setAlert({ variant: 'success', message: 'Run deleted.' });
      loadJobs(page);
    } else {
      showError(response.message);
    }
  };

  const handleSetupFolder = async (configId: string) => {
    setFolderBusy(true);
    const response = isJenkins
      ? await setupJenkinsFolder(configId)
      : await setupMailHogFolder(configId);
    setFolderBusy(false);
    if (response.success) {
      setAlert({ variant: 'success', message: 'Backup folder is ready.' });
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  // ---- Config CRUD ---------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    setJenkinsForm(emptyJenkinsForm);
    setMailHogForm(emptyMailHogForm);
    setShowForm(true);
  };

  const openEdit = (config: JenkinsConfig | MailHogConfig) => {
    setEditing(config);
    if (isJenkins) {
      const jenkins = config as JenkinsConfig;
      setJenkinsForm({
        name: jenkins.name,
        baseUrl: jenkins.baseUrl,
        username: jenkins.username,
        apiToken: '',
        baseFolderPath: jenkins.baseFolderPath,
        active: jenkins.active,
      });
    } else {
      const mailhog = config as MailHogConfig;
      setMailHogForm({
        name: mailhog.name,
        apiBaseUrl: mailhog.apiBaseUrl,
        username: mailhog.username || '',
        password: '',
        sshHost: mailhog.sshHost || '',
        sshPort: mailhog.sshPort ?? 22,
        sshUsername: mailhog.sshUsername || '',
        sshPassword: '',
        sshPrivateKey: '',
        sshKeyPassphrase: '',
        baseFolderPath: mailhog.baseFolderPath,
        active: mailhog.active,
      });
    }
    setShowForm(true);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    const response = isJenkins
      ? editing
        ? await updateJenkinsConfig(editing.id, jenkinsForm)
        : await createJenkinsConfig(jenkinsForm)
      : editing
        ? await updateMailHogConfig(editing.id, mailHogForm)
        : await createMailHogConfig(mailHogForm);
    setSaving(false);
    if (response.success) {
      setAlert({ variant: 'success', message: editing ? 'Configuration updated.' : 'Configuration added.' });
      setShowForm(false);
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  const handleTestConfig = async (config: JenkinsConfig | MailHogConfig) => {
    setTestingId(config.id);
    const response = isJenkins
      ? await testJenkinsConfig(config.id)
      : await testMailHogConfig(config.id);
    setTestingId(null);
    if (response.success) {
      setAlert({ variant: 'success', message: `${label} responded — connection is good.` });
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  const handleDeleteConfig = async (id: string) => {
    setConfirmConfigId(null);
    const response = isJenkins ? await deleteJenkinsConfig(id) : await deleteMailHogConfig(id);
    if (response.success) {
      setAlert({ variant: 'success', message: 'Configuration deleted.' });
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  const folderBadge = (config: JenkinsConfig | MailHogConfig) => (
    <span
      className={`flex items-center gap-2 text-xs font-bold ${
        config.folderExists ? 'text-emerald-400' : 'text-slate-400'
      }`}
    >
      <FolderCheck size={14} />
      {config.folderExists ? 'Folder ready' : 'Folder not found'}
      <code className={config.folderExists ? 'text-emerald-300' : 'text-slate-500'}>
        {config.baseFolderPath}
      </code>
    </span>
  );

  const tabs: { key: 'backups' | 'config'; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
    { key: 'backups', label: 'Backups', icon: FileDown },
    { key: 'config', label: `${label} Config`, icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}

      {/* Tabs ------------------------------------------------------- */}
      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
                active
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                  : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
              }`}
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* ================= TAB: BACKUPS ================= */}
      {tab === 'backups' && (
        <div className="space-y-6">
          {/* Trigger */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  {isJenkins ? <Wrench size={15} className="text-cyan-400" /> : <Mail size={15} className="text-cyan-400" />}{' '}
                  Run a {label} backup
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  {isJenkins
                    ? "Every job's config.xml plus credentials-store metadata, read over the Jenkins REST API."
                    : 'Every stored message as .eml plus raw JSON, read over the MailHog HTTP API.'}
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <Field label={`${label} configuration`}>
                <select
                  value={selectedId}
                  onChange={(event) => resetPage(() => setSelectedId(event.target.value))}
                  className={fieldClass}
                >
                  {configs.length === 0 && <option value="">No configuration yet</option>}
                  {configs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {selected && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                {folderBadge(selected)}
                <div className="flex items-center gap-2">
                  {!selected.folderExists && (
                    <button
                      type="button"
                      onClick={() => handleSetupFolder(selected.id)}
                      disabled={folderBusy}
                      className="inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-amber-300 transition hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {folderBusy ? (
                        <LoaderCircle size={13} className="animate-spin" />
                      ) : (
                        <FolderPlus size={13} />
                      )}
                      Setup Folder
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleTakeBackup}
                    disabled={!canTakeBackup || taking}
                    title={canTakeBackup ? 'Start the backup' : 'Set up the backup folder first'}
                    className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {taking ? (
                      <LoaderCircle size={14} className="animate-spin" />
                    ) : (
                      <FileDown size={14} />
                    )}
                    {taking ? 'Starting' : 'Take Backup'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Live progress ------------------------------------------------ */}
          {runningJob && liveJob && (
            <div className="rounded-3xl border border-cyan-500/30 bg-cyan-500/5 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                    <LoaderCircle size={15} className="animate-spin text-cyan-400" />
                    {label} backup in progress — {liveJob.dbName || label}
                  </h2>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    {liveJob.processedTableCount ?? 0} of {liveJob.tableCount ?? 0} items ·{' '}
                    {(liveJob.processedRows ?? 0).toLocaleString()} written
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleStopRun}
                  disabled={stopping}
                  className="inline-flex items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/15 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {stopping ? (
                    <LoaderCircle size={13} className="animate-spin" />
                  ) : (
                    <Square size={12} fill="currentColor" />
                  )}
                  {stopping ? 'Stopping' : 'Stop'}
                </button>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <span>Progress</span>
                  <span className="text-cyan-400">
                    {(liveJob.progressPercentage ?? 0).toFixed(1)}%
                  </span>
                </div>
                <div className="mt-1.5 h-3 w-full overflow-hidden rounded-full border border-slate-800 bg-slate-950/70">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-[width] duration-700 ease-out"
                    style={{ width: `${Math.max(1, liveJob.progressPercentage ?? 0)}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-[10px] font-semibold text-slate-500">
                  <span>
                    Elapsed {formatDuration(liveJob.elapsedTimeMs)} · Remaining{' '}
                    <span className="text-slate-300">
                      {formatDuration(liveJob.estimatedTimeMs)}
                    </span>
                  </span>
                  <span>
                    {formatBytes(liveJob.sizeBytes)} written
                    {liveJob.status === 'PENDING' && ' · waiting for the worker'}
                  </span>
                </div>
              </div>

              {liveJob.auditLogs && liveJob.auditLogs.length > 0 && (
                <div className="mt-4">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Export log
                  </span>
                  <div className="mt-1.5 max-h-48 space-y-1 overflow-y-auto rounded-2xl border border-slate-800 bg-slate-950/70 p-3">
                    {[...liveJob.auditLogs].reverse().map((line, index) => (
                      <p
                        key={`${index}-${line}`}
                        className="font-mono text-[11px] leading-relaxed text-slate-400"
                      >
                        <span className="text-cyan-500">›</span> {line}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Listing */}
          <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 p-6">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <FileDown size={15} className="text-cyan-400" /> {label} backup history
                </h2>
                <p className="mt-1 text-[10px] font-semibold text-slate-500">
                  {jobs?.totalElements ?? 0} runs recorded
                </p>
              </div>
              <button
                type="button"
                onClick={() => loadJobs(page)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <RefreshCw size={13} className={jobsLoading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>

            {/* Filters */}
            <div className="grid gap-2 px-6 pb-5 md:grid-cols-3 lg:grid-cols-5">
              <div className="relative md:col-span-1 lg:col-span-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                <input
                  value={search}
                  onChange={(event) => resetPage(() => setSearch(event.target.value))}
                  placeholder="Search name, folder or user"
                  className={`${fieldClass} pl-9`}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => resetPage(() => setStatusFilter(event.target.value))}
                className={fieldClass}
              >
                <option value="">All statuses</option>
                {BACKUP_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {BACKUP_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  From
                </span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => resetPage(() => setFromDate(event.target.value))}
                  className={`${fieldClass} min-w-0`}
                  aria-label="From date"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  To
                </span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => resetPage(() => setToDate(event.target.value))}
                  className={`${fieldClass} min-w-0`}
                  aria-label="To date"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-y border-slate-800 bg-slate-900/60">
                  <tr>
                    <th className={`${headerClass} text-left`}>Date</th>
                    <th className={`${headerClass} text-left`}>Source</th>
                    <th className={`${headerClass} text-left`}>Content</th>
                    <th className={`${headerClass} text-right`}>Items</th>
                    <th className={`${headerClass} text-right`}>Size</th>
                    <th className={`${headerClass} text-left`}>Status</th>
                    <th className={`${headerClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsLoading && !jobs ? (
                    Array.from({ length: 4 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="border-t border-slate-800/70">
                        {Array.from({ length: 7 }).map((__, colIndex) => (
                          <td key={colIndex} className="px-5 py-4">
                            <div className="h-4 w-full animate-pulse rounded bg-slate-800/80" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : !jobs || jobs.content.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
                            <FileDown size={24} />
                          </div>
                          <p className="text-sm font-black text-slate-200">No backups yet</p>
                          <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">
                            Configure {label} in the {label} Config tab, then take a backup.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    jobs.content.map((job) => (
                      <tr
                        key={job.id}
                        className="border-t border-slate-800/70 transition hover:bg-slate-900/40"
                      >
                        <td className="px-5 py-4">
                          <p className="text-xs font-bold text-slate-200">
                            {formatDateTime(job.createdAt)}
                          </p>
                          <p
                            className="max-w-[200px] truncate text-[10px] font-semibold text-slate-500"
                            title={job.folderPath || ''}
                          >
                            {job.folderPath || '—'}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs font-bold text-slate-200">{job.dbName || label}</p>
                          <span className="rounded-full border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                            {label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(job.formats || []).map((format) => (
                              <span
                                key={format}
                                className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-black tracking-wider text-cyan-400"
                              >
                                {format}
                              </span>
                            ))}
                            {!job.formats?.length && (
                              <span className="text-[10px] font-semibold text-slate-500">—</span>
                            )}
                          </div>
                          <p className="mt-1 text-[10px] font-semibold text-slate-500">
                            {job.tableCount ?? 0} {isJenkins ? 'jobs' : 'messages'}
                          </p>
                        </td>
                        <td className="px-5 py-4 text-right text-xs font-bold text-slate-300">
                          {(job.totalRows ?? 0).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right text-xs font-bold text-slate-300">
                          {formatBytes(job.sizeBytes)}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                              statusTone[job.status] || statusTone.PENDING
                            }`}
                          >
                            {(job.status === 'RUNNING' || job.status === 'PENDING') && (
                              <LoaderCircle size={11} className="animate-spin" />
                            )}
                            {BACKUP_STATUS_LABELS[job.status] || job.status}
                          </span>
                          {job.status === 'FAILED' && job.errorMessage && (
                            <p className="mt-1 max-w-[220px] text-[10px] font-semibold leading-tight text-rose-400">
                              {job.errorMessage}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {job.status === 'SUCCESS' && (
                              <button
                                type="button"
                                title="Download backup files (ZIP)"
                                onClick={() => handleDownload(job)}
                                disabled={downloadingId === job.id}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {downloadingId === job.id ? (
                                  <LoaderCircle size={14} className="animate-spin" />
                                ) : (
                                  <HardDriveDownload size={14} />
                                )}
                              </button>
                            )}
                            <button
                              type="button"
                              title="View audit log"
                              onClick={() => openJobLogs(job)}
                              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                            >
                              <ScrollText size={14} />
                            </button>
                            {confirmJobId === job.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteJob(job.id)}
                                  className="rounded-lg border border-rose-500/40 bg-rose-500/15 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25"
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmJobId(null)}
                                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                title="Delete run"
                                onClick={() => setConfirmJobId(job.id)}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {jobs && jobs.totalPages > 1 && <Pagination {...jobs} setPage={setPage} />}
          </div>
        </div>
      )}

      {/* ================= TAB: CONFIG ================= */}
      {tab === 'config' && (
        <div className="space-y-6">
          {/* Toolbar + form */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <Settings size={15} className="text-cyan-400" /> {label} configurations
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  {isJenkins
                    ? 'The controller URL and a user/API-token pair that can read job config.xml files.'
                    : "The base URL of MailHog's HTTP API (default http://localhost:8025)."}
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400"
              >
                <Plus size={14} /> Add configuration
              </button>
            </div>

            {showForm && (
              <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/60 p-5">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Name">
                    <input
                      value={isJenkins ? jenkinsForm.name : mailHogForm.name}
                      onChange={(event) =>
                        isJenkins
                          ? setJenkinsForm({ ...jenkinsForm, name: event.target.value })
                          : setMailHogForm({ ...mailHogForm, name: event.target.value })
                      }
                      className={fieldClass}
                      placeholder="e.g. Staging Jenkins"
                    />
                  </Field>
                  {isJenkins ? (
                    <>
                      <Field label="Jenkins URL">
                        <input
                          value={jenkinsForm.baseUrl}
                          onChange={(event) =>
                            setJenkinsForm({ ...jenkinsForm, baseUrl: event.target.value })
                          }
                          className={fieldClass}
                          placeholder="http://jenkins.example.com:8080"
                        />
                      </Field>
                      <Field label="Username">
                        <input
                          value={jenkinsForm.username}
                          onChange={(event) =>
                            setJenkinsForm({ ...jenkinsForm, username: event.target.value })
                          }
                          className={fieldClass}
                          placeholder="backup-bot"
                        />
                      </Field>
                      <Field
                        label="API token"
                        hint={
                          editing
                            ? 'Leave blank to keep the stored token.'
                            : 'Found under User > Configure > API Token in Jenkins.'
                        }
                      >
                        <input
                          type="password"
                          value={jenkinsForm.apiToken || ''}
                          onChange={(event) =>
                            setJenkinsForm({ ...jenkinsForm, apiToken: event.target.value })
                          }
                          className={fieldClass}
                          placeholder={editing ? '••••••••' : 'Jenkins API token'}
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="MailHog API URL">
                        <input
                          value={mailHogForm.apiBaseUrl}
                          onChange={(event) =>
                            setMailHogForm({ ...mailHogForm, apiBaseUrl: event.target.value })
                          }
                          className={fieldClass}
                          placeholder="http://localhost:8025"
                        />
                      </Field>
                      <Field
                        label="Username"
                        hint="Only when a proxy in front of MailHog requires Basic auth. Leave blank for a plain MailHog."
                      >
                        <input
                          value={mailHogForm.username || ''}
                          onChange={(event) =>
                            setMailHogForm({ ...mailHogForm, username: event.target.value })
                          }
                          className={fieldClass}
                          placeholder="Optional"
                        />
                      </Field>
                      <Field
                        label="Password"
                        hint={
                          editing && (editing as MailHogConfig).passwordSet
                            ? 'Leave blank to keep the stored password.'
                            : undefined
                        }
                      >
                        <input
                          type="password"
                          value={mailHogForm.password || ''}
                          onChange={(event) =>
                            setMailHogForm({ ...mailHogForm, password: event.target.value })
                          }
                          className={fieldClass}
                          placeholder={
                            editing && (editing as MailHogConfig).passwordSet ? '••••••••' : 'Optional'
                          }
                        />
                      </Field>
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 lg:col-span-2">
                        <p className="mb-3 text-[10px] font-black uppercase tracking-wider text-slate-500">
                          SSH — server configuration capture
                        </p>
                        <p className="mb-4 text-[10px] font-semibold text-slate-500">
                          Optional. When set, each backup also pulls the nginx site config,
                          systemd unit and install/data folder inventory over SSH.
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <Field label="SSH host">
                            <input
                              value={mailHogForm.sshHost || ''}
                              onChange={(event) =>
                                setMailHogForm({ ...mailHogForm, sshHost: event.target.value })
                              }
                              className={fieldClass}
                              placeholder="srv1076821.nexgent.tech"
                            />
                          </Field>
                          <Field label="SSH port">
                            <input
                              type="number"
                              min={1}
                              max={65535}
                              value={mailHogForm.sshPort ?? 22}
                              onChange={(event) => {
                                const value = event.target.valueAsNumber;
                                setMailHogForm({
                                  ...mailHogForm,
                                  sshPort: Number.isNaN(value) ? undefined : value,
                                });
                              }}
                              className={fieldClass}
                            />
                          </Field>
                          <Field label="SSH username">
                            <input
                              value={mailHogForm.sshUsername || ''}
                              onChange={(event) =>
                                setMailHogForm({ ...mailHogForm, sshUsername: event.target.value })
                              }
                              className={fieldClass}
                              placeholder="root"
                            />
                          </Field>
                          <Field
                            label="SSH password"
                            hint={
                              editing && (editing as MailHogConfig).sshPasswordSet
                                ? 'Leave blank to keep the stored password.'
                                : 'Use password OR private key.'
                            }
                          >
                            <input
                              type="password"
                              value={mailHogForm.sshPassword || ''}
                              onChange={(event) =>
                                setMailHogForm({ ...mailHogForm, sshPassword: event.target.value })
                              }
                              className={fieldClass}
                              placeholder={
                                editing && (editing as MailHogConfig).sshPasswordSet
                                  ? '••••••••'
                                  : 'Optional'
                              }
                            />
                          </Field>
                          <Field
                            label="SSH private key"
                            hint={
                              editing && (editing as MailHogConfig).sshKeySet
                                ? 'Leave blank to keep the stored key.'
                                : 'Paste an OpenSSH PEM key.'
                            }
                          >
                            <textarea
                              value={mailHogForm.sshPrivateKey || ''}
                              onChange={(event) =>
                                setMailHogForm({ ...mailHogForm, sshPrivateKey: event.target.value })
                              }
                              rows={4}
                              className={`${fieldClass} font-mono text-[10px]`}
                              placeholder={
                                editing && (editing as MailHogConfig).sshKeySet
                                  ? '••••••••'
                                  : '-----BEGIN OPENSSH PRIVATE KEY-----'
                              }
                            />
                          </Field>
                          <Field
                            label="Key passphrase"
                            hint={
                              editing && (editing as MailHogConfig).sshKeySet
                                ? 'Leave blank to keep the stored passphrase.'
                                : undefined
                            }
                          >
                            <input
                              type="password"
                              value={mailHogForm.sshKeyPassphrase || ''}
                              onChange={(event) =>
                                setMailHogForm({ ...mailHogForm, sshKeyPassphrase: event.target.value })
                              }
                              className={fieldClass}
                              placeholder={
                                editing && (editing as MailHogConfig).sshKeySet ? '••••••••' : 'Optional'
                              }
                            />
                          </Field>
                        </div>
                      </div>
                    </>
                  )}
                  <Field label="Backup folder path" hint="Use Setup Folder after saving if it is missing.">
                    <input
                      value={isJenkins ? jenkinsForm.baseFolderPath : mailHogForm.baseFolderPath}
                      onChange={(event) =>
                        isJenkins
                          ? setJenkinsForm({ ...jenkinsForm, baseFolderPath: event.target.value })
                          : setMailHogForm({ ...mailHogForm, baseFolderPath: event.target.value })
                      }
                      className={fieldClass}
                      placeholder="D:\backups\jenkins"
                    />
                  </Field>
                  <label className="flex items-center gap-2 self-end pb-2 text-xs font-bold text-slate-300">
                    <input
                      type="checkbox"
                      checked={isJenkins ? jenkinsForm.active !== false : mailHogForm.active !== false}
                      onChange={(event) =>
                        isJenkins
                          ? setJenkinsForm({ ...jenkinsForm, active: event.target.checked })
                          : setMailHogForm({ ...mailHogForm, active: event.target.checked })
                      }
                      className="h-4 w-4 rounded border-slate-600 bg-slate-950"
                    />
                    Active
                  </label>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:text-slate-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveConfig}
                    disabled={
                      saving ||
                      (isJenkins
                        ? !jenkinsForm.name.trim() ||
                          !jenkinsForm.baseUrl.trim() ||
                          !jenkinsForm.username.trim() ||
                          !jenkinsForm.baseFolderPath.trim() ||
                          (!editing && !jenkinsForm.apiToken?.trim())
                        : !mailHogForm.name.trim() ||
                          !mailHogForm.apiBaseUrl.trim() ||
                          !mailHogForm.baseFolderPath.trim() ||
                          (!!mailHogForm.sshHost?.trim() && !mailHogForm.sshUsername?.trim()))
                    }
                    className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {saving ? <LoaderCircle size={13} className="animate-spin" /> : null}
                    {editing ? 'Save changes' : 'Add configuration'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Config list */}
          <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
            {configs.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
                  <Settings size={24} />
                </div>
                <p className="text-sm font-black text-slate-200">No {label} configuration yet</p>
                <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">
                  Add one to enable {label} backups from this screen.
                </p>
              </div>
            ) : (
              configs.map((config) => (
                <div
                  key={config.id}
                  className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-800/70 px-6 py-5 first:border-t-0"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="flex items-center gap-2 text-sm font-black text-slate-100">
                      {config.name}
                      {!config.active && (
                        <span className="rounded-full border border-slate-600 bg-slate-800/60 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-slate-400">
                          Disabled
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs font-semibold text-slate-400">
                      {isJenkins
                        ? `${(config as JenkinsConfig).baseUrl} — user ${(config as JenkinsConfig).username}`
                        : `${(config as MailHogConfig).apiBaseUrl}${
                            (config as MailHogConfig).username
                              ? ` — user ${(config as MailHogConfig).username}`
                              : ''
                          }${
                            (config as MailHogConfig).sshHost
                              ? ` · ssh ${(config as MailHogConfig).sshUsername}@${(config as MailHogConfig).sshHost}`
                              : ''
                          }`}
                    </p>
                    {folderBadge(config)}
                    <p className="text-[10px] font-semibold text-slate-600">
                      Added {formatDateTime(config.createdAt)}
                      {isJenkins && !(config as JenkinsConfig).tokenSet && ' — API token missing'}
                      {!isJenkins &&
                        (config as MailHogConfig).username &&
                        !(config as MailHogConfig).passwordSet &&
                        ' — password missing'}
                      {!isJenkins &&
                        (config as MailHogConfig).sshHost &&
                        !(config as MailHogConfig).sshPasswordSet &&
                        !(config as MailHogConfig).sshKeySet &&
                        ' — ssh credentials missing'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleTestConfig(config)}
                      disabled={testingId === config.id}
                      title="Test the connection"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {testingId === config.id ? (
                        <LoaderCircle size={12} className="animate-spin" />
                      ) : (
                        <TestTubeDiagonal size={12} />
                      )}
                      Test
                    </button>
                    {!config.folderExists && (
                      <button
                        type="button"
                        onClick={() => handleSetupFolder(config.id)}
                        disabled={folderBusy}
                        className="inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-amber-300 transition hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {folderBusy ? (
                          <LoaderCircle size={12} className="animate-spin" />
                        ) : (
                          <FolderPlus size={12} />
                        )}
                        Setup Folder
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openEdit(config)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    {confirmConfigId === config.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleDeleteConfig(config.id)}
                          className="rounded-xl border border-rose-500/40 bg-rose-500/15 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmConfigId(null)}
                          className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        title="Delete configuration"
                        onClick={() => setConfirmConfigId(config.id)}
                        className="rounded-xl border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Audit log modal --------------------------------------------- */}
      {logsJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-800 bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <h3 className="flex items-center gap-2 text-sm font-black text-slate-100">
                <ScrollText size={15} className="text-cyan-400" /> Audit log
              </h3>
              <button
                type="button"
                onClick={() => {
                  setLogsJobId(null);
                  setLogsJob(null);
                }}
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:text-slate-200"
              >
                ✕
              </button>
            </div>
            <div className="max-h-[60vh] space-y-1 overflow-y-auto p-6">
              {logsLoading ? (
                <p className="text-xs font-semibold text-slate-500">Loading…</p>
              ) : logsJob?.auditLogs && logsJob.auditLogs.length > 0 ? (
                logsJob.auditLogs.map((line, index) => (
                  <p
                    key={`${index}-${line}`}
                    className="font-mono text-[11px] leading-relaxed text-slate-400"
                  >
                    <span className="text-cyan-500">›</span> {line}
                  </p>
                ))
              ) : (
                <p className="text-xs font-semibold text-slate-500">
                  No audit lines were recorded for this run.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SourceBackupComponent;
